import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CompanySearchFilter } from '../../../models/companySearchFilter';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CountryFlat } from '../../../models/country';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { MaterialModule } from '../../../shared/material.module';
import { Index } from '../../../models';
import { Helpers } from '../../../utilities/helpers';
import { TreeSelectComponent } from "../../shared/tree-select/tree-select.component";
import { Industry, IndustryFlat } from '../../../models/industry';
import { CurrentUserState } from '../../../state/current-user.state';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { Category } from '../../../models/enums/category.enum';

export interface CompanySearchFieldsConfig {
  showMeasurement?: boolean;
  showCountry?: boolean;
  showListingStatus?: boolean;
  showIndex?: boolean;
  showName?: boolean;
  showCode?: boolean;
  showIndustry?: boolean;
  showUnitaryDual?: boolean;
  showCapitalization?: boolean;
  showIsEmpty?: boolean;
  showCategory?: boolean;
  showFinished?: boolean;
  showChecked?: boolean;
  showIsin?: boolean;
  showIsActive?: boolean;
}

@Component({
  selector: 'app-company-search-box',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TreeSelectComponent
],
  templateUrl: './company-search-box.component.html',
  styleUrl: './company-search-box.component.scss'
})
export class CompanySearchBoxComponent implements OnChanges {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: CompanySearchFilter = {};
  @Input() fieldsConfig: CompanySearchFieldsConfig = {
    showMeasurement: true,
    showCountry: true,
    showListingStatus: true,
    showIndex: true,
    showName: true,
    showCode: true,
    showIndustry: true,
    showUnitaryDual: true,
    showCapitalization: true,
    showIsEmpty: true,
    showCategory: true,
    showFinished: true,
    showChecked: true,
    showIsin: true,
    showIsActive: true
  };
  @Output() companySearchFilter = new EventEmitter<CompanySearchFilter>();
  @ViewChild(TreeSelectComponent) treeSelectComponent!: TreeSelectComponent<Industry>;

  private currentUserState = inject(CurrentUserState);
  isCategoryLocked = (this.currentUserState.user()?.role === AppUserRole.Leader
    || this.currentUserState.user()?.role === AppUserRole.User)
    && this.currentUserState.user()?.category !== Category.None;
  showCompensationGatingInfo = (this.currentUserState.user()?.role === AppUserRole.Leader
    || this.currentUserState.user()?.role === AppUserRole.User)
    && this.currentUserState.user()?.category === Category.Compensation;
  showCsrGatingInfo = (this.currentUserState.user()?.role === AppUserRole.Leader
    || this.currentUserState.user()?.role === AppUserRole.User)
    && this.currentUserState.user()?.category === Category.Csr;
  showVotingResultsGatingInfo = (this.currentUserState.user()?.role === AppUserRole.Leader
    || this.currentUserState.user()?.role === AppUserRole.User)
    && this.currentUserState.user()?.category === Category.VotingResults;

  countries$: Observable<CountryFlat[]>;
  indexes$: Observable<Index[]>;
  industries$: Observable<Industry[]>;
  measurements: string[] = this.currentUserState.accessibleMeasurements();

  searchForm: {
    name: string | undefined;
    code: string | undefined;
    address: string | undefined;
    city: string | undefined;
    state: string | undefined;
    zip: string | undefined;
    country: string | undefined;
    isin: string | undefined;
    measurement: string | undefined;
    listingStatus: string | undefined;
    index: string | undefined;
    industry: string | undefined;
    unitaryDual: number | undefined;
    capitalization: number | undefined;
    isEmpty: string | undefined;
    category: number | undefined;
    finished: string | undefined;
    checked: string | undefined;
    isActive: boolean | undefined;
  } = {
    name: undefined,
    code: undefined,
    address: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    country: undefined,
    isin: undefined,
    measurement: this.measurements[0],
    listingStatus: undefined,
    index: undefined,
    industry: undefined,
    unitaryDual: undefined,
    capitalization: undefined,
    isEmpty: undefined,
    category: this.isCategoryLocked ? this.currentUserState.user()?.category : undefined,
    finished: undefined,
    checked: undefined,
    isActive: true
  };

  constructor(private lookupDataService: LookupDataService) {
    const userCountryIds = new Set(
      (this.currentUserState.user()?.countries ?? []).map(c => c.id)
    );

    this.countries$ = this.lookupDataService.getVisibleCountries<CountryFlat>().pipe(
      map(countries => countries
        .filter(country => userCountryIds.has(country.id))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );

    this.indexes$ = this.lookupDataService.getTableStream<Index>('stockIndices').pipe(
      map(indexes => {
        if (!indexes) return [];
        return indexes.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      })
    );

    this.industries$ = this.lookupDataService.getTableStream<IndustryFlat>('industries').pipe(
      map(flatIndustries => {
        if (!flatIndustries) return [];
        const hierarchical = Helpers.convertFlatToHierarchical(flatIndustries);
        return Helpers.sortIndustriesAlphabetically(hierarchical);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.applyInitialFilter();
    }
  }

  ngOnInit(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.applyInitialFilter();
    }
  }

  private applyInitialFilter(): void {
    this.searchForm = {
      name: this.initialFilter.name,
      code: this.initialFilter.code,
      address: this.initialFilter.address,
      city: this.initialFilter.city,
      state: this.initialFilter.state,
      zip: this.initialFilter.zip,
      country: this.initialFilter.country,
      isin: this.initialFilter.isin,
      measurement: this.initialFilter.measurement,
      listingStatus: this.initialFilter.listingStatus,
      index: this.initialFilter.index,
      industry: this.initialFilter.industry,
      unitaryDual: this.initialFilter.unitaryDual,
      capitalization: this.initialFilter.capitalization,
      isEmpty: undefined,
      category: this.isCategoryLocked ? this.currentUserState.user()?.category : this.initialFilter.category,
      finished: this.initialFilter.finished,
      checked: this.initialFilter.checked,
      isActive: this.initialFilter.isActive
    };
  }

  onSearch(): void {
    this.companySearchFilter.emit({ ...this.searchForm });
  }

  clearSearch(): void {
    this.searchForm = {
      name: undefined,
      code: undefined,
      address: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
      country: undefined,
      isin: undefined,
      measurement: this.measurements[0],
      listingStatus: undefined,
      index: undefined,
      industry: undefined,
      unitaryDual: undefined,
      capitalization: undefined,
      isEmpty: undefined,
      category: this.isCategoryLocked ? this.currentUserState.user()?.category : undefined,
      finished: undefined,
      checked: undefined,
      isActive: true
    };
    // Clear the tree-select component selection
    if (this.treeSelectComponent) {
      this.treeSelectComponent.clearSelection();
    }
    this.companySearchFilter.emit({ ...this.searchForm });
  }

  onCategoryChanged(): void {
    if (this.searchForm.category === undefined || this.searchForm.category === null) {
      this.searchForm.finished = undefined;
      this.searchForm.checked = undefined;
    }
  }

  onCategoryCleared(): void {
    this.searchForm.category = undefined;
    this.searchForm.finished = undefined;
    this.searchForm.checked = undefined;
  }

  onSelectionChange(selectedIndustry: any) {
    this.searchForm.industry = selectedIndustry ? selectedIndustry.id : undefined;
  }
}
